import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Props = {
  /** For signup, we persist the intended role so we can apply it after callback. */
  intendedRole?: "freelancer" | "client";
  className?: string;
};

/**
 * "Continue with Google" button.
 * - Uses Supabase OAuth (state validated by Supabase, secret stays server-side).
 * - Persists intended signup role and pending plan resume path.
 * - Returns to /auth so the existing post-login effect routes to dashboard or checkout.
 */
export function GoogleAuthButton({ intendedRole, className }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    setBusy(true);
    try {
      if (intendedRole) {
        try {
          window.localStorage.setItem("pending_signup_role", intendedRole);
        } catch {}
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: { prompt: "select_account" },
        },
      });
      if (error) {
        setBusy(false);
        toast.error(error.message || t("auth.googleError", "Google sign-in failed. Please try again."));
      }
      // On success, the browser is redirected away.
    } catch (err) {
      setBusy(false);
      toast.error(err instanceof Error ? err.message : "Google sign-in failed");
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={
        "group relative flex w-full items-center justify-center gap-3 h-12 rounded-full " +
        "border border-border/70 bg-background/70 backdrop-blur-md text-sm font-medium " +
        "text-foreground shadow-[0_10px_30px_-18px_color-mix(in_oklab,var(--primary)_60%,transparent)] " +
        "transition-all hover:bg-background/90 hover:border-primary/40 hover:scale-[1.01] " +
        "disabled:opacity-60 disabled:cursor-not-allowed " +
        (className ?? "")
      }
      aria-label={t("auth.continueWithGoogle", "Continue with Google")}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <GoogleGlyph className="h-5 w-5 shrink-0" />
      )}
      <span>{t("auth.continueWithGoogle", "Continue with Google")}</span>
    </button>
  );
}

export function GoogleDivider() {
  const { t } = useTranslation();
  return (
    <div className="my-5 flex items-center gap-3">
      <span className="h-px flex-1 bg-border/70" />
      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {t("auth.or", "or")}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}
